"use client";

import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "motion/react";
import { useTheme } from "next-themes";

import { cn } from "@/lib/utils";

const ENV_TEXTURE_SRC = "/lens/glass/royal-esplanade-2048.jpg";
const PHOTO_TEXTURE_SRC = "/images/moondream_color.png";

const CAMERA_DISTANCE = 7;
const FAR = 30;
const FOV = Math.PI / 4;
const GLASS_BEVEL_SEGMENTS = 8;
const GLASS_BEVEL_SIZE = 0.08;
const GLASS_BEVEL_THICKNESS = 0.05;
const GLASS_CONVEX_HEIGHT = 0.1;
const GLASS_DEPTH = 0.1;
const GLASS_FACE_SEGMENTS = 12;
const GLASS_RADIUS = 1.1;
const HALF_DEPTH = GLASS_DEPTH * 0.5;
const MAX_DPR = 1.5;
const NEAR = 0.1;
const PHOTO_SCALE = 1.2;
const RADIAL_SEGMENTS = 128;

const PHOTO_VERTEX_SHADER = `
attribute vec3 a_position;
attribute vec3 a_normal;
attribute vec2 a_uv;

uniform mat4 u_model;
uniform mat4 u_projection;
uniform mat4 u_view;

varying vec3 v_world_normal;
varying vec3 v_world_position;
varying vec2 v_uv;

void main() {
  vec4 world = u_model * vec4(a_position, 1.0);
  v_world_position = world.xyz;
  v_world_normal = mat3(u_model) * a_normal;
  v_uv = a_uv;
  gl_Position = u_projection * u_view * world;
}`;

const PHOTO_FRAGMENT_SHADER = `
precision highp float;

uniform vec3 u_camera_position;
uniform sampler2D u_texture;

varying vec3 v_world_normal;
varying vec3 v_world_position;
varying vec2 v_uv;

vec3 acesApprox(vec3 value) {
  value = max(value, vec3(0.0));
  return clamp(
    (value * (2.51 * value + 0.03)) / (value * (2.43 * value + 0.59) + 0.14),
    0.0,
    1.0
  );
}

void main() {
  vec3 normal = normalize(gl_FrontFacing ? v_world_normal : -v_world_normal);
  vec4 texel = texture2D(u_texture, v_uv);
  vec3 base = texel.rgb;

  vec3 lightBack = normalize(vec3(-5.0, 2.0, -10.0) - v_world_position);
  vec3 lightTop = normalize(vec3(0.0, 10.0, 0.0) - v_world_position);
  vec3 lightFront = normalize(vec3(0.0, 2.0, 10.0) - v_world_position);
  vec3 viewDir = normalize(u_camera_position - v_world_position);

  float diffuse =
    max(dot(normal, lightBack), 0.0) * 0.28 +
    max(dot(normal, lightTop), 0.0) * 0.22 +
    max(dot(normal, lightFront), 0.0) * 0.16;

  float specular = pow(max(dot(normal, normalize(lightFront + viewDir)), 0.0), 36.0) * 0.04;

  vec3 lit = base * (0.82 + diffuse) + vec3(specular);
  gl_FragColor = vec4(acesApprox(lit), texel.a);
}`;

const GLASS_VERTEX_SHADER = `
attribute vec3 a_position;
attribute vec3 a_normal;

uniform mat4 u_model;
uniform mat4 u_projection;
uniform mat4 u_view;

varying vec4 v_clip_position;
varying vec3 v_model_position;
varying vec3 v_world_normal;
varying vec3 v_world_position;

void main() {
  vec4 world = u_model * vec4(a_position, 1.0);
  v_model_position = a_position;
  v_world_position = world.xyz;
  v_world_normal = mat3(u_model) * a_normal;
  v_clip_position = u_projection * u_view * world;
  gl_Position = v_clip_position;
}`;

const GLASS_FRAGMENT_SHADER = `
precision highp float;

uniform vec3 u_background;
uniform vec3 u_camera_position;
uniform sampler2D u_env_texture;
uniform float u_env_intensity;
uniform float u_ior;
uniform sampler2D u_scene_texture;

varying vec4 v_clip_position;
varying vec3 v_model_position;
varying vec3 v_world_normal;
varying vec3 v_world_position;

const float PI = 3.141592653589793;
const float TAU = 6.283185307179586;

vec3 acesApprox(vec3 value) {
  value = max(value, vec3(0.0));
  return clamp(
    (value * (2.51 * value + 0.03)) / (value * (2.43 * value + 0.59) + 0.14),
    0.0,
    1.0
  );
}

float luminance(vec3 color) {
  return dot(color, vec3(0.2126, 0.7152, 0.0722));
}

vec2 envUv(vec3 dir) {
  dir = normalize(dir);
  float u = atan(dir.z, dir.x) / TAU + 0.5;
  float v = acos(clamp(dir.y, -1.0, 1.0)) / PI;
  return vec2(u, v);
}

vec3 sceneColor(vec2 uv) {
  vec4 sampleColor = texture2D(u_scene_texture, clamp(uv, vec2(0.001), vec2(0.999)));
  return mix(u_background, sampleColor.rgb, sampleColor.a);
}

void main() {
  vec3 normal = normalize(gl_FrontFacing ? v_world_normal : -v_world_normal);
  vec3 viewDir = normalize(u_camera_position - v_world_position);
  vec2 lensUv = v_model_position.xy / ${GLASS_RADIUS.toFixed(1)} * 0.5 + 0.5;

  vec2 screenUv = v_clip_position.xy / max(v_clip_position.w, 0.0001) * 0.5 + 0.5;
  vec3 refracted = refract(-viewDir, normal, 1.0 / u_ior);
  vec3 reflected = reflect(-viewDir, normal);
  float backgroundLuma = luminance(u_background);
  float darkBoost = 1.0 - smoothstep(0.06, 0.38, backgroundLuma);

  float ndotv = max(dot(normal, viewDir), 0.0);
  float fresnel = pow(1.0 - ndotv, 5.0);
  float edgeGlow = pow(1.0 - ndotv, 1.85);
  float radial = smoothstep(0.62, 1.0, length(v_model_position.xy) / ${GLASS_RADIUS.toFixed(1)});
  float profile = smoothstep(0.04, 0.24, abs(v_model_position.z));

  vec2 refractionOffset = refracted.xy * (0.018 + radial * 0.055 + edgeGlow * 0.04);
  vec3 transmitted = sceneColor(screenUv + refractionOffset);
  vec3 internalScene = sceneColor(screenUv + refractionOffset * 0.45);
  transmitted = mix(internalScene, transmitted, 0.72);

  vec3 envReflection = texture2D(u_env_texture, envUv(reflected)).rgb;
  vec3 envInternal = texture2D(u_env_texture, envUv(refracted)).rgb;
  vec3 darkLift = envInternal * (0.18 + radial * 0.12) + vec3(0.08, 0.085, 0.1);
  transmitted = mix(
    transmitted,
    max(transmitted, darkLift),
    darkBoost * (0.42 + radial * 0.18 + profile * 0.14)
  );

  vec3 lightBack = normalize(vec3(-5.0, 2.0, -10.0) - v_world_position);
  vec3 lightTop = normalize(vec3(0.0, 10.0, 0.0) - v_world_position);
  vec3 lightFront = normalize(vec3(0.0, 2.0, 10.0) - v_world_position);

  float specular =
    pow(max(dot(normal, normalize(lightFront + viewDir)), 0.0), 96.0) * 0.42 +
    pow(max(dot(normal, normalize(lightTop + viewDir)), 0.0), 84.0) * 0.18 +
    pow(max(dot(normal, normalize(lightBack + viewDir)), 0.0), 72.0) * 0.12;

  float rim = smoothstep(0.28, 1.0, edgeGlow) * (0.5 + radial * 0.5);
  float faceFade = smoothstep(0.02, 0.2, lensUv.y) * (1.0 - smoothstep(0.8, 0.98, lensUv.y));

  vec3 absorption = exp(-vec3(0.22, 0.15, 0.05) * (0.55 + profile * 2.4 + radial * 1.3));
  vec3 color = transmitted * absorption;
  color *= 0.84 + faceFade * 0.05 + darkBoost * 0.08;
  color += envInternal * (0.035 + profile * 0.04 + radial * 0.025) * u_env_intensity * (1.0 + darkBoost * 0.4);
  color = mix(
    color,
    envReflection * u_env_intensity,
    0.18 + fresnel * 0.76 + radial * 0.12 + darkBoost * 0.12
  );
  color += vec3(1.0) * specular * (1.0 + darkBoost * 0.45);
  color += mix(vec3(0.9, 0.95, 1.0), vec3(1.0), 0.5) * rim * (0.045 + darkBoost * 0.085);

  gl_FragColor = vec4(acesApprox(color), 1.0);
}`;

type ThemeColor = {
  css: string;
  rgb: [number, number, number];
};

type ThemePalette = {
  background: ThemeColor;
  foreground: ThemeColor;
};

type GeometryData = {
  indices: Uint16Array;
  normals: Float32Array;
  positions: Float32Array;
  uvs?: Float32Array;
};

type MeshBuffers = {
  count: number;
  index: WebGLBuffer;
  normal: WebGLBuffer;
  position: WebGLBuffer;
  uv?: WebGLBuffer;
};

type RenderTarget = {
  colorTexture: WebGLTexture;
  depthBuffer: WebGLRenderbuffer;
  framebuffer: WebGLFramebuffer;
};

const FALLBACK_PALETTES: Record<"dark" | "light", ThemePalette> = {
  dark: {
    background: { css: "rgb(10, 10, 11)", rgb: [10, 10, 11] },
    foreground: { css: "rgb(250, 250, 250)", rgb: [250, 250, 250] },
  },
  light: {
    background: { css: "rgb(255, 255, 255)", rgb: [255, 255, 255] },
    foreground: { css: "rgb(24, 24, 27)", rgb: [24, 24, 27] },
  },
};

function compileShader(
  gl: WebGLRenderingContext,
  type: number,
  source: string
): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) {
    return null;
  }

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }

  return shader;
}

function createProgram(
  gl: WebGLRenderingContext,
  vertexSource: string,
  fragmentSource: string
): WebGLProgram | null {
  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);

  if (!vertexShader || !fragmentShader) {
    return null;
  }

  const program = gl.createProgram();
  if (!program) {
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);
    return null;
  }

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);

  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error(gl.getProgramInfoLog(program));
    gl.deleteProgram(program);
    return null;
  }

  return program;
}

function resolveColor(
  probe: HTMLDivElement,
  variable: string,
  fallback: ThemeColor
): ThemeColor {
  probe.style.color = `var(${variable}, ${fallback.css})`;
  const resolved = window.getComputedStyle(probe).color;
  const parts = resolved.match(/[\d.]+/g);

  if (!parts || parts.length < 3) {
    return fallback;
  }

  return {
    css: resolved,
    rgb: [
      Math.round(Number(parts[0])),
      Math.round(Number(parts[1])),
      Math.round(Number(parts[2])),
    ],
  };
}

function readPalette(theme: "dark" | "light"): ThemePalette {
  const fallback = FALLBACK_PALETTES[theme];
  const probe = document.createElement("div");
  probe.style.opacity = "0";
  probe.style.pointerEvents = "none";
  probe.style.position = "absolute";
  document.body.appendChild(probe);

  const palette = {
    background: resolveColor(probe, "--background", fallback.background),
    foreground: resolveColor(probe, "--foreground", fallback.foreground),
  };

  probe.remove();
  return palette;
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Unable to load ${src}`));
    image.src = src;
  });
}

function createTexture(gl: WebGLRenderingContext, image: HTMLImageElement) {
  const texture = gl.createTexture();
  if (!texture) {
    return null;
  }

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
  gl.bindTexture(gl.TEXTURE_2D, null);

  return texture;
}

function createRenderTarget(
  gl: WebGLRenderingContext,
  width: number,
  height: number
): RenderTarget | null {
  const colorTexture = gl.createTexture();
  const framebuffer = gl.createFramebuffer();
  const depthBuffer = gl.createRenderbuffer();

  if (!colorTexture || !framebuffer || !depthBuffer) {
    return null;
  }

  gl.bindTexture(gl.TEXTURE_2D, colorTexture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGBA,
    width,
    height,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    null
  );

  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
  gl.framebufferTexture2D(
    gl.FRAMEBUFFER,
    gl.COLOR_ATTACHMENT0,
    gl.TEXTURE_2D,
    colorTexture,
    0
  );

  gl.bindRenderbuffer(gl.RENDERBUFFER, depthBuffer);
  gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, width, height);
  gl.framebufferRenderbuffer(
    gl.FRAMEBUFFER,
    gl.DEPTH_ATTACHMENT,
    gl.RENDERBUFFER,
    depthBuffer
  );

  const isComplete = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;

  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.bindRenderbuffer(gl.RENDERBUFFER, null);
  gl.bindTexture(gl.TEXTURE_2D, null);

  if (!isComplete) {
    gl.deleteTexture(colorTexture);
    gl.deleteRenderbuffer(depthBuffer);
    gl.deleteFramebuffer(framebuffer);
    return null;
  }

  return {
    colorTexture,
    depthBuffer,
    framebuffer,
  };
}

function destroyRenderTarget(gl: WebGLRenderingContext, target: RenderTarget | null) {
  if (!target) {
    return;
  }

  gl.deleteTexture(target.colorTexture);
  gl.deleteRenderbuffer(target.depthBuffer);
  gl.deleteFramebuffer(target.framebuffer);
}

function normalize3(x: number, y: number, z: number) {
  const length = Math.hypot(x, y, z) || 1;
  return [x / length, y / length, z / length] as const;
}

function cross3(
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number
) {
  return [
    ay * bz - az * by,
    az * bx - ax * bz,
    ax * by - ay * bx,
  ] as const;
}

function dot3(
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number
) {
  return ax * bx + ay * by + az * bz;
}

function multiplyMatrix4(
  out: Float32Array,
  a: Float32Array,
  b: Float32Array
) {
  const a00 = a[0];
  const a01 = a[1];
  const a02 = a[2];
  const a03 = a[3];
  const a10 = a[4];
  const a11 = a[5];
  const a12 = a[6];
  const a13 = a[7];
  const a20 = a[8];
  const a21 = a[9];
  const a22 = a[10];
  const a23 = a[11];
  const a30 = a[12];
  const a31 = a[13];
  const a32 = a[14];
  const a33 = a[15];

  const b00 = b[0];
  const b01 = b[1];
  const b02 = b[2];
  const b03 = b[3];
  const b10 = b[4];
  const b11 = b[5];
  const b12 = b[6];
  const b13 = b[7];
  const b20 = b[8];
  const b21 = b[9];
  const b22 = b[10];
  const b23 = b[11];
  const b30 = b[12];
  const b31 = b[13];
  const b32 = b[14];
  const b33 = b[15];

  out[0] = a00 * b00 + a10 * b01 + a20 * b02 + a30 * b03;
  out[1] = a01 * b00 + a11 * b01 + a21 * b02 + a31 * b03;
  out[2] = a02 * b00 + a12 * b01 + a22 * b02 + a32 * b03;
  out[3] = a03 * b00 + a13 * b01 + a23 * b02 + a33 * b03;
  out[4] = a00 * b10 + a10 * b11 + a20 * b12 + a30 * b13;
  out[5] = a01 * b10 + a11 * b11 + a21 * b12 + a31 * b13;
  out[6] = a02 * b10 + a12 * b11 + a22 * b12 + a32 * b13;
  out[7] = a03 * b10 + a13 * b11 + a23 * b12 + a33 * b13;
  out[8] = a00 * b20 + a10 * b21 + a20 * b22 + a30 * b23;
  out[9] = a01 * b20 + a11 * b21 + a21 * b22 + a31 * b23;
  out[10] = a02 * b20 + a12 * b21 + a22 * b22 + a32 * b23;
  out[11] = a03 * b20 + a13 * b21 + a23 * b22 + a33 * b23;
  out[12] = a00 * b30 + a10 * b31 + a20 * b32 + a30 * b33;
  out[13] = a01 * b30 + a11 * b31 + a21 * b32 + a31 * b33;
  out[14] = a02 * b30 + a12 * b31 + a22 * b32 + a32 * b33;
  out[15] = a03 * b30 + a13 * b31 + a23 * b32 + a33 * b33;
}

function setPerspectiveMatrix(
  out: Float32Array,
  fov: number,
  aspect: number,
  near: number,
  far: number
) {
  const f = 1 / Math.tan(fov / 2);

  out[0] = f / aspect;
  out[1] = 0;
  out[2] = 0;
  out[3] = 0;
  out[4] = 0;
  out[5] = f;
  out[6] = 0;
  out[7] = 0;
  out[8] = 0;
  out[9] = 0;
  out[10] = (far + near) / (near - far);
  out[11] = -1;
  out[12] = 0;
  out[13] = 0;
  out[14] = (2 * far * near) / (near - far);
  out[15] = 0;
}

function setLookAtMatrix(
  out: Float32Array,
  eye: readonly number[],
  target: readonly number[],
  up: readonly number[]
) {
  const [zx, zy, zz] = normalize3(
    eye[0] - target[0],
    eye[1] - target[1],
    eye[2] - target[2]
  );
  const [xx0, xy0, xz0] = cross3(up[0], up[1], up[2], zx, zy, zz);
  const [xx, xy, xz] = normalize3(xx0, xy0, xz0);
  const [yx, yy, yz] = cross3(zx, zy, zz, xx, xy, xz);

  out[0] = xx;
  out[1] = yx;
  out[2] = zx;
  out[3] = 0;
  out[4] = xy;
  out[5] = yy;
  out[6] = zy;
  out[7] = 0;
  out[8] = xz;
  out[9] = yz;
  out[10] = zz;
  out[11] = 0;
  out[12] = -dot3(xx, xy, xz, eye[0], eye[1], eye[2]);
  out[13] = -dot3(yx, yy, yz, eye[0], eye[1], eye[2]);
  out[14] = -dot3(zx, zy, zz, eye[0], eye[1], eye[2]);
  out[15] = 1;
}

function setRotationXMatrix(out: Float32Array, angle: number) {
  const c = Math.cos(angle);
  const s = Math.sin(angle);

  out[0] = 1;
  out[1] = 0;
  out[2] = 0;
  out[3] = 0;
  out[4] = 0;
  out[5] = c;
  out[6] = s;
  out[7] = 0;
  out[8] = 0;
  out[9] = -s;
  out[10] = c;
  out[11] = 0;
  out[12] = 0;
  out[13] = 0;
  out[14] = 0;
  out[15] = 1;
}

function setRotationYMatrix(out: Float32Array, angle: number) {
  const c = Math.cos(angle);
  const s = Math.sin(angle);

  out[0] = c;
  out[1] = 0;
  out[2] = -s;
  out[3] = 0;
  out[4] = 0;
  out[5] = 1;
  out[6] = 0;
  out[7] = 0;
  out[8] = s;
  out[9] = 0;
  out[10] = c;
  out[11] = 0;
  out[12] = 0;
  out[13] = 0;
  out[14] = 0;
  out[15] = 1;
}

function setRotationZMatrix(out: Float32Array, angle: number) {
  const c = Math.cos(angle);
  const s = Math.sin(angle);

  out[0] = c;
  out[1] = s;
  out[2] = 0;
  out[3] = 0;
  out[4] = -s;
  out[5] = c;
  out[6] = 0;
  out[7] = 0;
  out[8] = 0;
  out[9] = 0;
  out[10] = 1;
  out[11] = 0;
  out[12] = 0;
  out[13] = 0;
  out[14] = 0;
  out[15] = 1;
}

function setScaleMatrix(out: Float32Array, x: number, y: number, z: number) {
  out[0] = x;
  out[1] = 0;
  out[2] = 0;
  out[3] = 0;
  out[4] = 0;
  out[5] = y;
  out[6] = 0;
  out[7] = 0;
  out[8] = 0;
  out[9] = 0;
  out[10] = z;
  out[11] = 0;
  out[12] = 0;
  out[13] = 0;
  out[14] = 0;
  out[15] = 1;
}

function createPhotoGeometry(image: HTMLImageElement): GeometryData {
  const aspectRatio = image.naturalWidth / image.naturalHeight;
  const width = aspectRatio > 1 ? PHOTO_SCALE : PHOTO_SCALE * aspectRatio;
  const height = aspectRatio > 1 ? PHOTO_SCALE / aspectRatio : PHOTO_SCALE;
  const halfWidth = width * 0.5;
  const halfHeight = height * 0.5;

  return {
    indices: new Uint16Array([0, 1, 2, 0, 2, 3]),
    normals: new Float32Array([
      0, 0, 1,
      0, 0, 1,
      0, 0, 1,
      0, 0, 1,
    ]),
    positions: new Float32Array([
      -halfWidth, -halfHeight, 0,
      halfWidth, -halfHeight, 0,
      halfWidth, halfHeight, 0,
      -halfWidth, halfHeight, 0,
    ]),
    uvs: new Float32Array([
      0, 0,
      1, 0,
      1, 1,
      0, 1,
    ]),
  };
}

function accumulateNormals(
  positions: Float32Array,
  indices: number[],
  normals: Float32Array
) {
  for (let index = 0; index < indices.length; index += 3) {
    const a = indices[index] * 3;
    const b = indices[index + 1] * 3;
    const c = indices[index + 2] * 3;

    const ax = positions[a];
    const ay = positions[a + 1];
    const az = positions[a + 2];
    const bx = positions[b];
    const by = positions[b + 1];
    const bz = positions[b + 2];
    const cx = positions[c];
    const cy = positions[c + 1];
    const cz = positions[c + 2];

    const abx = bx - ax;
    const aby = by - ay;
    const abz = bz - az;
    const acx = cx - ax;
    const acy = cy - ay;
    const acz = cz - az;

    const [nx, ny, nz] = cross3(abx, aby, abz, acx, acy, acz);

    normals[a] += nx;
    normals[a + 1] += ny;
    normals[a + 2] += nz;
    normals[b] += nx;
    normals[b + 1] += ny;
    normals[b + 2] += nz;
    normals[c] += nx;
    normals[c + 1] += ny;
    normals[c + 2] += nz;
  }

  for (let index = 0; index < normals.length; index += 3) {
    const [nx, ny, nz] = normalize3(
      normals[index],
      normals[index + 1],
      normals[index + 2]
    );
    normals[index] = nx;
    normals[index + 1] = ny;
    normals[index + 2] = nz;
  }
}

function createCircularLensGeometry(radialSegments = RADIAL_SEGMENTS): GeometryData {
  const faceRadius = GLASS_RADIUS - GLASS_BEVEL_SIZE;
  const profile: Array<{ radius: number; z: number }> = [];

  for (let step = 0; step <= GLASS_BEVEL_SEGMENTS; step += 1) {
    const t = step / GLASS_BEVEL_SEGMENTS;
    profile.push({
      radius: faceRadius + GLASS_BEVEL_SIZE * Math.sin((t * Math.PI) / 2),
      z: -HALF_DEPTH + GLASS_BEVEL_THICKNESS * (1 - Math.cos((t * Math.PI) / 2)),
    });
  }

  profile.push({ radius: GLASS_RADIUS, z: 0 });

  for (let step = GLASS_BEVEL_SEGMENTS; step >= 0; step -= 1) {
    const t = step / GLASS_BEVEL_SEGMENTS;
    profile.push({
      radius: faceRadius + GLASS_BEVEL_SIZE * Math.sin((t * Math.PI) / 2),
      z: HALF_DEPTH - GLASS_BEVEL_THICKNESS * (1 - Math.cos((t * Math.PI) / 2)),
    });
  }

  const sidePositions: number[] = [];
  const sideIndices: number[] = [];

  for (let ringIndex = 0; ringIndex < profile.length; ringIndex += 1) {
    const ring = profile[ringIndex];

    for (let segment = 0; segment < radialSegments; segment += 1) {
      const angle = (segment / radialSegments) * Math.PI * 2;
      sidePositions.push(
        Math.cos(angle) * ring.radius,
        Math.sin(angle) * ring.radius,
        ring.z
      );
    }
  }

  for (let ringIndex = 0; ringIndex < profile.length - 1; ringIndex += 1) {
    const ringOffset = ringIndex * radialSegments;
    const nextRingOffset = (ringIndex + 1) * radialSegments;

    for (let segment = 0; segment < radialSegments; segment += 1) {
      const nextSegment = (segment + 1) % radialSegments;
      const a = ringOffset + segment;
      const b = ringOffset + nextSegment;
      const c = nextRingOffset + segment;
      const d = nextRingOffset + nextSegment;

      sideIndices.push(a, b, c, b, d, c);
    }
  }

  const sideNormals = new Float32Array(sidePositions.length);
  const indices = [...sideIndices];

  accumulateNormals(new Float32Array(sidePositions), sideIndices, sideNormals);

  const positions = [...sidePositions];
  const normals = Array.from(sideNormals);

  const addCap = (front: boolean) => {
    const capPositions: number[] = [];
    const capIndices: number[] = [];
    const ringOffsets: number[] = [];
    const direction = front ? 1 : -1;
    const edgeZ = direction * HALF_DEPTH;
    const poleZ = direction * (HALF_DEPTH + GLASS_CONVEX_HEIGHT);

    capPositions.push(0, 0, poleZ);

    for (let ring = 1; ring <= GLASS_FACE_SEGMENTS; ring += 1) {
      const t = ring / GLASS_FACE_SEGMENTS;
      const radius = faceRadius * t;
      const curvature = 1 - t * t;
      const z = edgeZ + direction * GLASS_CONVEX_HEIGHT * curvature;

      ringOffsets.push(capPositions.length / 3);

      for (let segment = 0; segment < radialSegments; segment += 1) {
        const angle = (segment / radialSegments) * Math.PI * 2;
        capPositions.push(Math.cos(angle) * radius, Math.sin(angle) * radius, z);
      }
    }

    const firstRing = ringOffsets[0];

    for (let segment = 0; segment < radialSegments; segment += 1) {
      const nextSegment = (segment + 1) % radialSegments;
      const current = firstRing + segment;
      const next = firstRing + nextSegment;

      if (front) {
        capIndices.push(0, current, next);
      } else {
        capIndices.push(0, next, current);
      }
    }

    for (let ring = 0; ring < ringOffsets.length - 1; ring += 1) {
      const innerStart = ringOffsets[ring];
      const outerStart = ringOffsets[ring + 1];

      for (let segment = 0; segment < radialSegments; segment += 1) {
        const nextSegment = (segment + 1) % radialSegments;
        const innerCurrent = innerStart + segment;
        const innerNext = innerStart + nextSegment;
        const outerCurrent = outerStart + segment;
        const outerNext = outerStart + nextSegment;

        if (front) {
          capIndices.push(
            innerCurrent,
            outerCurrent,
            innerNext,
            innerNext,
            outerCurrent,
            outerNext
          );
        } else {
          capIndices.push(
            innerCurrent,
            innerNext,
            outerCurrent,
            innerNext,
            outerNext,
            outerCurrent
          );
        }
      }
    }

    const capNormals = new Float32Array(capPositions.length);
    accumulateNormals(new Float32Array(capPositions), capIndices, capNormals);

    const baseIndex = positions.length / 3;
    positions.push(...capPositions);
    normals.push(...Array.from(capNormals));

    for (const index of capIndices) {
      indices.push(baseIndex + index);
    }
  };

  addCap(false);
  addCap(true);

  return {
    indices: new Uint16Array(indices),
    normals: new Float32Array(normals),
    positions: new Float32Array(positions),
  };
}

function createMeshBuffers(
  gl: WebGLRenderingContext,
  geometry: GeometryData
): MeshBuffers | null {
  const position = gl.createBuffer();
  const normal = gl.createBuffer();
  const index = gl.createBuffer();

  if (!position || !normal || !index) {
    return null;
  }

  gl.bindBuffer(gl.ARRAY_BUFFER, position);
  gl.bufferData(gl.ARRAY_BUFFER, geometry.positions, gl.STATIC_DRAW);

  gl.bindBuffer(gl.ARRAY_BUFFER, normal);
  gl.bufferData(gl.ARRAY_BUFFER, geometry.normals, gl.STATIC_DRAW);

  let uv: WebGLBuffer | undefined;
  if (geometry.uvs) {
    uv = gl.createBuffer() ?? undefined;
    if (!uv) {
      return null;
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, uv);
    gl.bufferData(gl.ARRAY_BUFFER, geometry.uvs, gl.STATIC_DRAW);
  }

  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, index);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, geometry.indices, gl.STATIC_DRAW);

  return {
    count: geometry.indices.length,
    index,
    normal,
    position,
    uv,
  };
}

function destroyMeshBuffers(gl: WebGLRenderingContext, mesh: MeshBuffers | null) {
  if (!mesh) {
    return;
  }

  gl.deleteBuffer(mesh.position);
  gl.deleteBuffer(mesh.normal);
  gl.deleteBuffer(mesh.index);

  if (mesh.uv) {
    gl.deleteBuffer(mesh.uv);
  }
}

function HeroFallback({ ready }: { ready: boolean }) {
  return (
    <div
      className={cn(
        "absolute inset-0 transition-opacity duration-500",
        "opacity-0"
      )}
    >
      <div
        className="absolute left-1/2 top-1/2 h-[72%] w-[72%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-foreground/10 bg-transparent shadow-[0_28px_70px_-48px_rgba(15,23,42,0.35)]"
      />
      <div
        className="absolute left-[39%] top-[28%] h-[16%] w-[16%] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.75),rgba(255,255,255,0.05)_70%,transparent)] blur-2xl"
      />
      <div className="absolute left-1/2 top-1/2 h-[72%] w-[72%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle_at_33%_24%,rgba(255,255,255,0.32),transparent_15%),radial-gradient(circle_at_68%_64%,rgba(255,255,255,0.08),transparent_21%),linear-gradient(180deg,rgba(255,255,255,0.1),rgba(255,255,255,0.02))]" />
    </div>
  );
}

function toUniformColor(rgb: [number, number, number]) {
  return [rgb[0] / 255, rgb[1] / 255, rgb[2] / 255] as const;
}

export function LensGlassHero() {
  const shouldReduceMotion = useReducedMotion();
  const { resolvedTheme } = useTheme();
  const theme = resolvedTheme === "dark" ? "dark" : "light";
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawRef = useRef<(() => void) | null>(null);
  const paletteRef = useRef<ThemePalette | null>(null);
  const pointerTargetRef = useRef({ x: 0, y: 0 });
  const pointerCurrentRef = useRef({ x: 0, y: 0 });
  const readyRef = useRef(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    paletteRef.current = readPalette(theme);
    drawRef.current?.();
  }, [theme]);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;

    if (!container || !canvas) {
      return;
    }

    const gl = canvas.getContext("webgl", {
      alpha: true,
      antialias: true,
      depth: true,
      desynchronized: true,
      powerPreference: "high-performance",
      premultipliedAlpha: false,
      preserveDrawingBuffer: false,
      stencil: false,
    });

    if (!gl) {
      return;
    }

    const photoProgram = createProgram(gl, PHOTO_VERTEX_SHADER, PHOTO_FRAGMENT_SHADER);
    const glassProgram = createProgram(gl, GLASS_VERTEX_SHADER, GLASS_FRAGMENT_SHADER);

    if (!photoProgram || !glassProgram) {
      return;
    }

    const photoAttributes = {
      normal: gl.getAttribLocation(photoProgram, "a_normal"),
      position: gl.getAttribLocation(photoProgram, "a_position"),
      uv: gl.getAttribLocation(photoProgram, "a_uv"),
    };
    const photoUniforms = {
      cameraPosition: gl.getUniformLocation(photoProgram, "u_camera_position"),
      model: gl.getUniformLocation(photoProgram, "u_model"),
      projection: gl.getUniformLocation(photoProgram, "u_projection"),
      texture: gl.getUniformLocation(photoProgram, "u_texture"),
      view: gl.getUniformLocation(photoProgram, "u_view"),
    };
    const glassAttributes = {
      normal: gl.getAttribLocation(glassProgram, "a_normal"),
      position: gl.getAttribLocation(glassProgram, "a_position"),
    };
    const glassUniforms = {
      background: gl.getUniformLocation(glassProgram, "u_background"),
      cameraPosition: gl.getUniformLocation(glassProgram, "u_camera_position"),
      envIntensity: gl.getUniformLocation(glassProgram, "u_env_intensity"),
      envTexture: gl.getUniformLocation(glassProgram, "u_env_texture"),
      ior: gl.getUniformLocation(glassProgram, "u_ior"),
      model: gl.getUniformLocation(glassProgram, "u_model"),
      projection: gl.getUniformLocation(glassProgram, "u_projection"),
      sceneTexture: gl.getUniformLocation(glassProgram, "u_scene_texture"),
      view: gl.getUniformLocation(glassProgram, "u_view"),
    };

    const hasPhotoBindings =
      photoAttributes.position >= 0 &&
      photoAttributes.normal >= 0 &&
      photoAttributes.uv >= 0 &&
      photoUniforms.cameraPosition &&
      photoUniforms.model &&
      photoUniforms.projection &&
      photoUniforms.texture &&
      photoUniforms.view;

    const hasGlassBindings =
      glassAttributes.position >= 0 &&
      glassAttributes.normal >= 0 &&
      glassUniforms.background &&
      glassUniforms.cameraPosition &&
      glassUniforms.envIntensity &&
      glassUniforms.envTexture &&
      glassUniforms.ior &&
      glassUniforms.model &&
      glassUniforms.projection &&
      glassUniforms.sceneTexture &&
      glassUniforms.view;

    if (!hasPhotoBindings || !hasGlassBindings) {
      return;
    }

    const projectionMatrix = new Float32Array(16);
    const viewMatrix = new Float32Array(16);
    const modelRotationX = new Float32Array(16);
    const modelRotationY = new Float32Array(16);
    const modelRotationZ = new Float32Array(16);
    const modelMatrix = new Float32Array(16);
    const scaleMatrix = new Float32Array(16);
    const tempMatrix = new Float32Array(16);
    const planeModelMatrix = new Float32Array(16);

    let renderTarget: RenderTarget | null = null;
    let photoTexture: WebGLTexture | null = null;
    let envTexture: WebGLTexture | null = null;
    let photoMesh: MeshBuffers | null = null;
    let glassMesh: MeshBuffers | null = null;

    let cleanupFinished = false;
    let frameId = 0;
    let visible = true;
    let width = 0;
    let height = 0;
    let elapsed = 0;
    let spin = 0;
    let lastFrame = performance.now();

    const cameraPosition = [0, 0, CAMERA_DISTANCE] as [number, number, number];

    const resize = () => {
      const rect = container.getBoundingClientRect();
      width = rect.width;
      height = rect.height;

      if (!width || !height) {
        return;
      }

      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      const nextWidth = Math.max(2, Math.round(width * dpr));
      const nextHeight = Math.max(2, Math.round(height * dpr));

      if (canvas.width !== nextWidth || canvas.height !== nextHeight) {
        canvas.width = nextWidth;
        canvas.height = nextHeight;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        destroyRenderTarget(gl, renderTarget);
        renderTarget = createRenderTarget(gl, nextWidth, nextHeight);
      }

      setPerspectiveMatrix(projectionMatrix, FOV, width / height, NEAR, FAR);
    };

    const bindPhotoMesh = () => {
      if (!photoMesh) {
        return;
      }

      gl.bindBuffer(gl.ARRAY_BUFFER, photoMesh.position);
      gl.enableVertexAttribArray(photoAttributes.position);
      gl.vertexAttribPointer(photoAttributes.position, 3, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ARRAY_BUFFER, photoMesh.normal);
      gl.enableVertexAttribArray(photoAttributes.normal);
      gl.vertexAttribPointer(photoAttributes.normal, 3, gl.FLOAT, false, 0, 0);

      if (photoMesh.uv) {
        gl.bindBuffer(gl.ARRAY_BUFFER, photoMesh.uv);
        gl.enableVertexAttribArray(photoAttributes.uv);
        gl.vertexAttribPointer(photoAttributes.uv, 2, gl.FLOAT, false, 0, 0);
      }

      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, photoMesh.index);
    };

    const bindGlassMesh = () => {
      if (!glassMesh) {
        return;
      }

      gl.bindBuffer(gl.ARRAY_BUFFER, glassMesh.position);
      gl.enableVertexAttribArray(glassAttributes.position);
      gl.vertexAttribPointer(glassAttributes.position, 3, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ARRAY_BUFFER, glassMesh.normal);
      gl.enableVertexAttribArray(glassAttributes.normal);
      gl.vertexAttribPointer(glassAttributes.normal, 3, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, glassMesh.index);
    };

    const updateTransforms = () => {
      const pointer = pointerCurrentRef.current;
      const xRotation = shouldReduceMotion ? 0.12 : Math.cos(elapsed * 0.4) * 0.2;
      const yRotation = shouldReduceMotion ? 0.42 : spin + 0.42;
      const zRotation = shouldReduceMotion ? 0.05 : Math.sin(elapsed * 0.21) * 0.1;

      setRotationXMatrix(modelRotationX, xRotation);
      setRotationYMatrix(modelRotationY, yRotation);
      setRotationZMatrix(modelRotationZ, zRotation);
      multiplyMatrix4(tempMatrix, modelRotationY, modelRotationX);
      multiplyMatrix4(modelMatrix, tempMatrix, modelRotationZ);

      const yaw = pointer.x * 0.18;
      const pitch = pointer.y * 0.12;
      cameraPosition[0] = Math.sin(yaw) * CAMERA_DISTANCE;
      cameraPosition[1] = Math.sin(pitch) * 1.75;
      cameraPosition[2] = Math.cos(yaw) * CAMERA_DISTANCE;
      setLookAtMatrix(viewMatrix, cameraPosition, [0, 0, 0], [0, 1, 0]);

      setScaleMatrix(scaleMatrix, 1, 1, 1);
      multiplyMatrix4(planeModelMatrix, modelMatrix, scaleMatrix);
    };

    const drawPhoto = () => {
      if (!photoMesh || !photoTexture) {
        return;
      }

      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.useProgram(photoProgram);
      bindPhotoMesh();
      gl.uniformMatrix4fv(photoUniforms.model, false, planeModelMatrix);
      gl.uniformMatrix4fv(photoUniforms.projection, false, projectionMatrix);
      gl.uniformMatrix4fv(photoUniforms.view, false, viewMatrix);
      gl.uniform3f(
        photoUniforms.cameraPosition,
        cameraPosition[0],
        cameraPosition[1],
        cameraPosition[2]
      );
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, photoTexture);
      gl.uniform1i(photoUniforms.texture, 0);
      gl.drawElements(gl.TRIANGLES, photoMesh.count, gl.UNSIGNED_SHORT, 0);
    };

    const drawGlass = () => {
      const palette = paletteRef.current;

      if (!glassMesh || !renderTarget || !envTexture || !palette) {
        return;
      }

      const background = toUniformColor(palette.background.rgb);
      const backgroundLuma =
        background[0] * 0.2126 + background[1] * 0.7152 + background[2] * 0.0722;
      const darkBoost = 1 - Math.min(Math.max((backgroundLuma - 0.06) / 0.32, 0), 1);

      gl.useProgram(glassProgram);
      bindGlassMesh();
      gl.uniformMatrix4fv(glassUniforms.model, false, modelMatrix);
      gl.uniformMatrix4fv(glassUniforms.projection, false, projectionMatrix);
      gl.uniformMatrix4fv(glassUniforms.view, false, viewMatrix);
      gl.uniform3f(
        glassUniforms.cameraPosition,
        cameraPosition[0],
        cameraPosition[1],
        cameraPosition[2]
      );
      gl.uniform3f(
        glassUniforms.background,
        background[0],
        background[1],
        background[2]
      );
      gl.uniform1f(glassUniforms.ior, 1.5);
      gl.uniform1f(glassUniforms.envIntensity, 1.2 + darkBoost * 0.45);

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, renderTarget.colorTexture);
      gl.uniform1i(glassUniforms.sceneTexture, 0);

      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, envTexture);
      gl.uniform1i(glassUniforms.envTexture, 1);

      gl.disable(gl.BLEND);
      gl.depthMask(true);
      gl.disable(gl.CULL_FACE);
      gl.drawElements(gl.TRIANGLES, glassMesh.count, gl.UNSIGNED_SHORT, 0);
    };

    const draw = () => {
      if (!renderTarget) {
        return;
      }

      updateTransforms();

      gl.enable(gl.DEPTH_TEST);
      gl.depthFunc(gl.LEQUAL);

      gl.bindFramebuffer(gl.FRAMEBUFFER, renderTarget.framebuffer);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      drawPhoto();

      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      drawPhoto();
      drawGlass();

      if (!readyRef.current) {
        readyRef.current = true;
        setIsReady(true);
      }
    };
    drawRef.current = draw;

    const tick = (now: number) => {
      if (!visible) {
        frameId = 0;
        return;
      }

      const delta = Math.min((now - lastFrame) * 0.001, 0.033);
      lastFrame = now;

      const current = pointerCurrentRef.current;
      const target = pointerTargetRef.current;
      current.x += (target.x - current.x) * 0.08;
      current.y += (target.y - current.y) * 0.08;

      if (!shouldReduceMotion) {
        elapsed += delta;
        spin += delta * 0.5;
      }

      draw();
      frameId = requestAnimationFrame(tick);
    };

    const handlePointerMove = (event: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;

      pointerTargetRef.current = {
        x: (x - 0.5) * 2,
        y: (0.5 - y) * 2,
      };
    };

    const handlePointerLeave = () => {
      pointerTargetRef.current = { x: 0, y: 0 };
    };

    const resizeObserver = new ResizeObserver(() => {
      resize();
      draw();
    });

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;

        if (visible) {
          lastFrame = performance.now();
          draw();

          if (!frameId) {
            frameId = requestAnimationFrame(tick);
          }
        } else if (frameId) {
          cancelAnimationFrame(frameId);
          frameId = 0;
        }
      },
      { threshold: 0 }
    );

    const contextLostHandler = (event: Event) => {
      event.preventDefault();
      readyRef.current = false;
      setIsReady(false);

      if (frameId) {
        cancelAnimationFrame(frameId);
        frameId = 0;
      }
    };

    canvas.addEventListener("webglcontextlost", contextLostHandler);
    container.addEventListener("pointermove", handlePointerMove);
    container.addEventListener("pointerleave", handlePointerLeave);

    Promise.all([loadImage(PHOTO_TEXTURE_SRC), loadImage(ENV_TEXTURE_SRC)])
      .then(([photoImage, envImage]) => {
        if (cleanupFinished) {
          return;
        }

        photoTexture = createTexture(gl, photoImage);
        envTexture = createTexture(gl, envImage);
        photoMesh = createMeshBuffers(gl, createPhotoGeometry(photoImage));
        glassMesh = createMeshBuffers(gl, createCircularLensGeometry());

        if (!photoTexture || !envTexture || !photoMesh || !glassMesh) {
          return;
        }

        resize();
        resizeObserver.observe(container);
        intersectionObserver.observe(container);
        draw();

        if (!shouldReduceMotion) {
          frameId = requestAnimationFrame(tick);
        }
      })
      .catch((error) => {
        console.error(error);
      });

    return () => {
      cleanupFinished = true;
      drawRef.current = null;
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      canvas.removeEventListener("webglcontextlost", contextLostHandler);
      container.removeEventListener("pointermove", handlePointerMove);
      container.removeEventListener("pointerleave", handlePointerLeave);

      destroyRenderTarget(gl, renderTarget);
      destroyMeshBuffers(gl, photoMesh);
      destroyMeshBuffers(gl, glassMesh);

      if (photoTexture) {
        gl.deleteTexture(photoTexture);
      }

      if (envTexture) {
        gl.deleteTexture(envTexture);
      }

      gl.deleteProgram(photoProgram);
      gl.deleteProgram(glassProgram);
    };
  }, [shouldReduceMotion]);

  return (
    <div className="relative mx-auto w-full">
      <div ref={containerRef} className="relative aspect-square w-full">
        <HeroFallback ready={isReady} />
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          className={cn(
            "absolute inset-0 size-full transition-opacity duration-500",
            isReady ? "opacity-100" : "opacity-0"
          )}
        />
      </div>
    </div>
  );
}
